
import { Player, TransferNews, Game, StandingItem, TransferGroup, NewsItem, Language, MatchDetail, MatchStat, MatchEvent, LeagueDB, PlayerSearchResult, TeamLineup, TeamColors } from '../types';
import { getFromCache, saveToCache, clearCache } from './storageService';
import { cleanUrl, formatMarketValue, convertTo24Hour, getEuropeanOffset, parseCurrencyString, calculateTimeAgo, getTeamLogo, getLeagueLogo } from './formatService';
import { supabase } from './authService';

export { getTeamLogo, getLeagueLogo };

export { formatCurrency, parseCurrencyString } from './formatService';
export type { LeagueDB, PlayerSearchResult };

const FOOTBALL_API_URL = 'https://v3.football.api-sports.io';
const FOOTBALL_API_KEY = import.meta.env.VITE_FOOTBALL_API_KEY; 
const CORS_PROXY = 'https://corsproxy.io/?';

export const fetchFootballAPI = async (endpoint: string): Promise<any> => {
    try {
        const url = `${FOOTBALL_API_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'x-apisports-key': FOOTBALL_API_KEY }
        });
        if (!response.ok) {
            const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
            const proxyRes = await fetch(proxyUrl, { headers: { 'x-apisports-key': FOOTBALL_API_KEY } });
            return await proxyRes.json();
        }
        return await response.json();
    } catch (error) {
        console.error("Football API Error:", error);
        return null;
    }
};

const MAJOR_LEAGUE_IDS = [1, 39, 140, 135, 78, 61, 88, 94, 203, 307, 253, 40, 292, 293, 98, 71, 262, 128, 144, 179, 10, 2, 3, 45, 48, 81, 143, 137, 66, 202, 235, 233, 236];
const BIG_5_IDS = [1, 39, 140, 135, 78, 61, 45, 48, 81, 143, 137, 66];

/**
 * DB에서 가져온 로우 데이터를 현재 언어 설정에 맞는 NewsItem 객체로 매핑합니다.
 * 해당 언어 필드가 없으면 영어 -> 한국어 순으로 폴백합니다.
 */
const mapRawNewsToItem = (item: any, lang: Language): NewsItem => {
    const getField = (base: string) => {
        // 우선순위: 선택 언어 -> 영어 -> 한국어
        const langField = `${base}_${lang}`;
        const enField = `${base}_en`;
        const koField = `${base}_ko`;
        
        return item[langField] || item[enField] || item[koField] || '';
    };

    return {
        id: item.id.toString(),
        title: getField('title'),
        summary: getField('summary'),
        content: getField('content'),
        audio_url: getField('audio_url'),
        time: calculateTimeAgo(item.published_at),
        publishedAt: item.published_at,
        image: item.image,
        source: item.source,
        type: item.type,
        url: item.url || '',
        videoUrl: item.video_url || undefined,
        sourceIcon: item.source_icon,
        players: item.players,
        teams: item.teams,
        user_id: item.user_id,
        is_reported: item.is_reported || false,
        is_hidden: item.is_hidden || false
    };
};

export const fetchLeagues = async (includeAll: boolean = false): Promise<LeagueDB[]> => {
    const cacheKey = includeAll ? 'all_leagues_v1' : 'major_leagues_v23_numeric';
    const cachedLeagues = getFromCache<LeagueDB[]>(cacheKey);
    if (cachedLeagues) return cachedLeagues;
    try {
        const res = await fetchFootballAPI('/leagues');
        if (res && res.response) {
            let leagues = res.response;
            
            if (!includeAll) {
                leagues = leagues.filter((item: any) => MAJOR_LEAGUE_IDS.includes(item.league.id));
            }

            const mapped: LeagueDB[] = leagues.map((item: any) => ({
                id: item.league.id.toString(),
                name: item.league.name,
                logo_url: getLeagueLogo(item.league.id),
                country: item.country.code || 'UN',
                order: MAJOR_LEAGUE_IDS.indexOf(item.league.id)
            }));

            if (!includeAll) {
                mapped.sort((a: LeagueDB, b: LeagueDB) => (a.order || 0) - (b.order || 0));
    } else {
        clearCache(cacheKey);
    }

            if (mapped.length > 0) saveToCache(cacheKey, mapped, 1000 * 60 * 60 * 24);
            return mapped;
        }
        return [];
    } catch (e) { return []; }
};

export const fetchMajorGamesToday = async (leagueIds?: string[]): Promise<Game[]> => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const yesterdayStr = new Date(now.getTime() - 86400000).toISOString().split('T')[0];
    
    const targetLeagues = leagueIds && leagueIds.length > 0 ? leagueIds.map(id => parseInt(id)) : BIG_5_IDS;
    
    const cacheKey = `major_games_recent_v2_${todayStr}_${targetLeagues.sort().join('_')}`;
    const cachedGames = getFromCache<Game[]>(cacheKey);
    if (cachedGames) return cachedGames;

    try {
        const [resToday, resYesterday] = await Promise.all([
            fetchFootballAPI(`/fixtures?date=${todayStr}`),
            fetchFootballAPI(`/fixtures?date=${yesterdayStr}`)
        ]);

        const combinedResponse = [
            ...(resToday?.response || []),
            ...(resYesterday?.response || [])
        ];

        if (combinedResponse.length === 0) return [];

        const nowTime = now.getTime();
        const cutoffTimeForFinished = nowTime - (14 * 60 * 60 * 1000); 

        const filtered = combinedResponse
            .filter((item: any) => targetLeagues.includes(item.league.id))
            .filter((item: any) => {
                // 리그 10 (친선/특정컵) 제외 필터링
                if (item.league.id === 10) {
                    const homeName = item.teams?.home?.name || '';
                    const awayName = item.teams?.away?.name || '';
                    const isYouth = /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(homeName) || /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(awayName);
                    if (isYouth) return false;
                }
                const startTime = new Date(item.fixture.date).getTime();
                const status = item.fixture.status.short;
                const isFinished = ['FT', 'AET', 'PEN'].includes(status);
                if (item.fixture.date.split('T')[0] === todayStr && !isFinished) return true;
                if (isFinished && startTime > cutoffTimeForFinished) return true;
                return false;
            })
            .map((item: any) => {
                const f = item.fixture; const t = item.teams; const g = item.goals;
                if (!f.date) return null; // Skip invalid fixtures
                let status: Game['status'] = 'Scheduled';
                if (['FT', 'AET', 'PEN'].includes(f.status.short)) status = 'Finished';
                else if (['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(f.status.short)) status = 'Live';

                return {
                    id: f.id.toString(),
                    league: item.league.id.toString(),
                    leagueName: item.league.name,
                    leagueLogo: getLeagueLogo(item.league.id),
                    homeTeam: { name: t.home.name, logo: getTeamLogo(t.home.id), id: t.home.id.toString() },
                    awayTeam: { name: t.away.name, logo: getTeamLogo(t.away.id), id: t.away.id.toString() },
                    time: new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
                    dateLabel: new Date(f.date).toLocaleDateString('sv-SE'), status,
                    score: status !== 'Scheduled' ? `${g.home}:${g.away}` : undefined,
                    currentMinute: f.status.elapsed?.toString(),
                    elapsed: f.status.elapsed,
                    round: item.league.round
                };
            }).filter(Boolean); // Remove null entries

        const uniqueMap = new Map();
        filtered.forEach((g: any) => uniqueMap.set(g.id, g));
        const uniqueGames = Array.from(uniqueMap.values());

        const sorted = uniqueGames.sort((a: any, b: any) => {
            const priority: any = { 'Live': 0, 'Finished': 1, 'Scheduled': 2, 'Replay': 3 };
            if (priority[a.status] !== priority[b.status]) return priority[a.status] - priority[b.status];
            return new Date(a.dateLabel + 'T' + a.time).getTime() - new Date(b.dateLabel + 'T' + b.time).getTime();
        });

        saveToCache(cacheKey, sorted, 1000 * 60 * 10); 
        return sorted;
    } catch (e) { return []; }
};

export const fetchPopularNews = async (lang: Language, limit: number = 3): Promise<NewsItem[]> => {
    try {
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        
        const { data: newsData, error } = await supabase
            .from('news_list')
            .select('*')
            .neq('type', 'gossip')
            .neq('type', 'stadium') // 스타디움 게시물 제외
            .neq('type', 'analysis') // 분석 게시품 제외
            .eq('is_hidden', false) // is_hidden이 false인 것만 필터링
            .gte('published_at', threeDaysAgo.toISOString())
            .order('published_at', { ascending: false })
            .limit(limit);

        if (error || !newsData) return [];

        const userIds = Array.from(new Set(newsData.filter(n => n.user_id).map(n => n.user_id)));
        const { data: userData } = userIds.length > 0 
            ? await supabase.from('user').select('id, name, avatar_url').in('id', userIds)
            : { data: [] };
        
        const userMap = new Map<string, any>((userData || []).map(u => [u.id, u]));

        return newsData.map((item: any) => {
            const newsItem = mapRawNewsToItem(item, lang);
            if (userMap.has(item.user_id)) {
                const u = userMap.get(item.user_id);
                newsItem.author = { name: u.name, avatar: u.avatar_url };
            }
            return newsItem;
        });
    } catch (e) { return []; }
};

export const searchPlayers = async (query: string): Promise<PlayerSearchResult[]> => {
    if (!query || query.trim().length < 3) return [];
    const cacheKey = `player_global_search_v4_smart_sort_${encodeURIComponent(query.trim())}`;
    const cachedResults = getFromCache<PlayerSearchResult[]>(cacheKey);
    if (cachedResults) return cachedResults;

    try {
        const q = query.trim().toLowerCase();
        let res = await fetchFootballAPI(`/players/profiles?search=${encodeURIComponent(q)}`);
        if (!res || !res.response || res.response.length === 0) {
            const CURRENT_SEASON = new Date().getMonth() >= 7 ? new Date().getFullYear() : new Date().getFullYear() - 1;
            res = await fetchFootballAPI(`/players?search=${encodeURIComponent(q)}&season=${CURRENT_SEASON}`);
        }

        if (res && res.response) {
            const mapped: PlayerSearchResult[] = res.response.map((item: any) => {
                const p = item.player;
                const s = item.statistics?.[0] || item.team;
                return {
                    id: p.id.toString(),
                    name: p.name,
                    team: s?.team?.name || s?.name || 'Professional Player', 
                    avatar: p.photo,
                    position: p.position || s?.games?.position,
                    nationality: p.nationality
                };
            });

            const sortedResults = mapped.map(player => {
                let score = 0;
                const playerName = player.name.toLowerCase();
                if (playerName === q) score += 100;
                else if (playerName.startsWith(q)) score += 50;
                if (playerName.includes(q)) score += 10;
                return { player, score };
            }).sort((a, b) => b.score - a.score || a.player.name.length - b.player.name.length)
              .map(item => item.player);
            
            if (sortedResults.length > 0) {
                saveToCache(cacheKey, sortedResults, 1000 * 60 * 60);
                return sortedResults;
            }
        }
    } catch (e) { console.error(e); }
    return [];
};

export const getPlayerDetails = async (id: string): Promise<Player | null> => {
    const cacheKey = `player_full_v3_5yr_fix_${id}`;
    const cachedPlayer = getFromCache<Player>(cacheKey);
    if (cachedPlayer) return cachedPlayer;

    try {
        const CURRENT_YEAR = new Date().getMonth() >= 7 ? new Date().getFullYear() : new Date().getFullYear() - 1;
        let realId = id;
        
        if (isNaN(Number(id)) && id.startsWith('search-')) {
            const raw = id.replace('search-', '');
            const [fullName] = raw.split('|');
            const playerRes = await fetchFootballAPI(`/players?search=${encodeURIComponent(fullName.trim())}&season=${CURRENT_YEAR}`);
            if (playerRes?.response?.length > 0) realId = playerRes.response[0].player.id.toString();
            else return null;
        }

        const profileRes = await fetchFootballAPI(`/players/profiles?player=${realId}`);
        const seasons = [CURRENT_YEAR, CURRENT_YEAR-1, CURRENT_YEAR-2, CURRENT_YEAR-3, CURRENT_YEAR-4];
        const responses = await Promise.all(seasons.map(s => fetchFootballAPI(`/players?id=${realId}&season=${s}`)));
        
        const validResponses = responses.filter(r => r?.response?.length > 0);
        if (validResponses.length === 0 && !profileRes?.response?.[0]) return null;

        const baseData = validResponses[0]?.response[0];
        const p = profileRes?.response?.[0]?.player || baseData?.player;
        if (!p) return null;

        const player: Player = {
            id: realId, name: p.name, shortName: p.lastname || p.name.split(' ').pop(),
            position: baseData?.statistics?.[0]?.games?.position || p.position || 'Unknown',
            team: baseData?.statistics?.[0]?.team?.name || 'Free Agent',
            currentTeamId: baseData?.statistics?.[0]?.team?.id?.toString(),
            teamLogo: getTeamLogo(baseData?.statistics?.[0]?.team?.id),
            avatar: p.photo, marketValue: 'Unknown', age: p.age?.toString(), nationality: p.nationality,
            height: p.height, weight: p.weight || 'Unknown', foot: p.foot || 'Unknown',
            stats: [], detailedStats: [], history: []
        };

        validResponses.forEach(res => {
            const data = res.response[0];
            const stats = data.statistics || [];
            player.stats.push({
                year: stats[0]?.league?.season,
                goals: stats.reduce((acc: number, s: any) => acc + (s.goals?.total || 0), 0),
                assists: stats.reduce((acc: number, s: any) => acc + (s.goals?.assists || 0), 0),
                appearances: stats.reduce((acc: number, s: any) => acc + (s.games?.appearences || 0), 0),
                minutesPlayed: stats.reduce((acc: number, s: any) => acc + (s.games?.minutes || 0), 0),
                rating: parseFloat(stats[0]?.games?.rating) || 0
            });
            stats.forEach((s: any) => {
                player.detailedStats!.push({
                    season: `${s.league?.season}/${String((s.league?.season || 0) + 1).slice(-2)}`,
                    competition: s.league?.name || 'Unknown', competitionLogo: getLeagueLogo(s.league?.id),
                    team: s.team?.name || 'Unknown', teamId: s.team?.id?.toString() || '',
                    goals: s.goals?.total || 0, assists: s.goals?.assists || 0,
                    appearances: s.games?.appearences || 0, minutesPlayed: s.games?.minutes || 0,
                    rating: parseFloat(s.games?.rating) || 0
                });
            });
        });

        const transRes = await fetchFootballAPI(`/transfers?player=${realId}`);
        if (transRes?.response?.[0]?.transfers) {
            player.history = transRes.response[0].transfers.map((t: any) => {
                console.log("Transfer Item:", t); // 디버깅용 로그
                const clubId = t.teams?.in?.id || t.teams?.in?.team_id || t.teams?.in?.id_team;
                return {
                    seasons: t.date?.split('-')[0] || 'Recent',
                    club: t.teams?.in?.name || 'Unknown', 
                    clubId: clubId?.toString(),
                    clubLogo: getTeamLogo(clubId),
                    fee: t.type || 'Transfer', 
                    type: t.type?.toLowerCase().includes('loan') ? 'loan' : (t.type?.toLowerCase().includes('free') ? 'free' : 'transfer')
                };
            });
        }

        saveToCache(cacheKey, player, 1000 * 60 * 60 * 6);
        return player;
    } catch (e) { return null; }
};

export const fetchTeamRecentMatches = async (teamId: string, limit: number = 5, leagueId?: string): Promise<Game[]> => {
    try {
        let endpoint = `/fixtures?team=${teamId}&last=${limit}`;
        if (leagueId) endpoint += `&league=${leagueId}`;
        const res = await fetchFootballAPI(endpoint);
        if (!res?.response) return [];
        return res.response.map((item: any) => {
            const f = item.fixture; const t = item.teams; const g = item.goals;
            let status: Game['status'] = 'Scheduled';
            if (['FT', 'AET', 'PEN'].includes(f.status.short)) status = 'Finished';
            else if (['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(f.status.short)) status = 'Live';
            return {
                id: f.id.toString(), league: item.league?.id?.toString() || 'unknown',
                homeTeam: { name: t.home.name, logo: getTeamLogo(t.home.id), id: t.home.id?.toString() },
                awayTeam: { name: t.away.name, logo: getTeamLogo(t.away.id), id: t.away.id?.toString() },
                time: new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
                dateLabel: new Date(f.date).toLocaleDateString('sv-SE'), status,
                score: status !== 'Scheduled' ? `${g.home}:${g.away}` : undefined, round: item.league.round
            };
        });
    } catch (e) { return []; }
};

export const fetchLeagueFixtures = async (leagueIdStr: string, season: string): Promise<Game[]> => {
    const leagueId = parseInt(leagueIdStr);
    if (isNaN(leagueId)) return [];
    
    const now = new Date();
    const todayStr = now.toLocaleDateString('sv-SE');
    const tomorrow = new Date(now.getTime() + 86400000);
    const tomorrowStr = tomorrow.toLocaleDateString('sv-SE');

    const cacheKey = `league_fixtures_${leagueId}_${season}`;
    const cachedFixtures = getFromCache<Game[]>(cacheKey);

    // 캐시가 있고 오늘/내일 데이터가 캐시에 없는 경우(실시간성 필요) API 재호출 유도
    if (cachedFixtures) {
        const hasTodayOrTomorrow = cachedFixtures.some(g => g.dateLabel === todayStr || g.dateLabel === tomorrowStr);
        if (!hasTodayOrTomorrow) return cachedFixtures;
    }

    const res = await fetchFootballAPI(`/fixtures?league=${leagueId}&season=${season}`);
    if (!res?.response) return cachedFixtures || [];

    const mapped: Game[] = res.response
        .filter((item: any) => {
            if (item.league.id === 10) {
                const homeName = item.teams?.home?.name || '';
                const awayName = item.teams?.away?.name || '';
                const isYouth = /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(homeName) || /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(awayName);
                if (isYouth) return false;
            }
            return true;
        })
        .map((item: any) => {
        const f = item.fixture; const t = item.teams; const g = item.goals;
        let status: Game['status'] = 'Scheduled';
        if (['FT', 'AET', 'PEN'].includes(f.status.short)) status = 'Finished';
        else if (['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(f.status.short)) status = 'Live';
        return {
            id: f.id.toString(), league: leagueIdStr,
            homeTeam: { name: t.home.name, logo: getTeamLogo(t.home.id), id: t.home.id.toString() },
            awayTeam: { name: t.away.name, logo: getTeamLogo(t.away.id), id: t.away.id.toString() },
            time: new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
            dateLabel: new Date(f.date).toLocaleDateString('sv-SE'), status,
            score: status !== 'Scheduled' ? `${g.home}:${g.away}` : undefined,
            elapsed: f.status.elapsed,
            round: item.league.round,
            statusShort: f.status.short,
            penalty: item.score?.penalty?.home != null ? `${item.score.penalty.home}:${item.score.penalty.away}` : undefined,
        };
    });

    if (mapped.length > 0) {
        // 오늘이나 내일 경기가 포함되어 있으면 캐시하지 않음 (실시간 정보 중요)
        const hasTodayOrTomorrow = mapped.some(g => g.dateLabel === todayStr || g.dateLabel === tomorrowStr);
        if (!hasTodayOrTomorrow) {
            saveToCache(cacheKey, mapped, 1000 * 60 * 60 * 6); // 6시간 캐시
        }
    }
    return mapped;
};

export const fetchGamesByLeagueAndDate = async (leagueId: string, date: string, season?: string): Promise<Game[]> => {
    const dateObj = new Date(date);
    const month = dateObj.getMonth(); // 0-indexed (0: Jan, 11: Dec)
    const year = dateObj.getFullYear();
    
    // CALENDAR_YEAR_LEAGUES: K League(1), MLS(253), J1 League(98), Brasileiro Série A(71), CONCACAF Champions League(16) 등
    const isCalendarYear = ['1', '292', '293', '253', '98', '71', '16', '10'].includes(leagueId);
    
    let targetSeason: string;
    if (season) {
        targetSeason = season;
    } else if (isCalendarYear) {
        targetSeason = year.toString();
    } else {
        // 유럽 리그 등 추동계 시즌 (8월 시작 기준)
        targetSeason = month >= 7 ? year.toString() : (year - 1).toString();
    }
    
    const now = new Date();
    const todayStr = now.toLocaleDateString('sv-SE');
    const tomorrow = new Date(now.getTime() + 86400000);
    const tomorrowStr = tomorrow.toLocaleDateString('sv-SE');
    const isTodayOrTomorrow = date === todayStr || date === tomorrowStr;

    const cacheKey = `games_${leagueId}_${date}_${targetSeason}`;
    if (!isTodayOrTomorrow) {
        const cached = getFromCache<Game[]>(cacheKey);
        if (cached) return cached;
    }

    try {
        const res = await fetchFootballAPI(`/fixtures?league=${leagueId}&date=${date}&season=${targetSeason}`);
        if (!res?.response) return [];

        const games: Game[] = res.response
            .filter((item: any) => {
                if (item.league.id === 10) {
                    const homeName = item.teams?.home?.name || '';
                    const awayName = item.teams?.away?.name || '';
                    const isYouth = /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(homeName) || /U[-]?\s*(17|18|19|20|21|22|23|24|25)\b/i.test(awayName);
                    if (isYouth) return false;
                }
                return true;
            })
            .map((item: any) => {
            const f = item.fixture; const t = item.teams; const g = item.goals;
            let status: Game['status'] = 'Scheduled';
            if (['FT', 'AET', 'PEN'].includes(f.status.short)) status = 'Finished';
            else if (['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(f.status.short)) status = 'Live';

            return {
                id: f.id.toString(),
                league: item.league.id.toString(),
                leagueName: item.league.name,
                leagueLogo: getLeagueLogo(item.league.id),
                homeTeam: { name: t.home.name, logo: getTeamLogo(t.home.id), id: t.home.id.toString() },
                awayTeam: { name: t.away.name, logo: getTeamLogo(t.away.id), id: t.away.id.toString() },
                time: new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
                dateLabel: new Date(f.date).toLocaleDateString('sv-SE'), status,
                score: status !== 'Scheduled' ? `${g.home}:${g.away}` : undefined,
                currentMinute: f.status.elapsed?.toString(),
                elapsed: f.status.elapsed,
                round: item.league.round
            };
        });

        if (!isTodayOrTomorrow) {
            saveToCache(cacheKey, games, 1000 * 60 * 30); // 30 mins cache
        }
        return games;
    } catch (e) {
        return [];
    }
};

export const fetchLeagueStandings = async (leagueIdStr: string, season: string): Promise<StandingItem[]> => {
    const leagueId = parseInt(leagueIdStr);
    if (isNaN(leagueId)) return [];

    let targetSeason = season;
    if (leagueId === 1 || leagueId === 292 || leagueId === 293 || leagueId === 10) {
        targetSeason = '2026';
    }

    const res = await fetchFootballAPI(`/standings?league=${leagueId}&season=${targetSeason}`);
    if (!res?.response?.[0]?.league?.standings?.[0]) return [];

    let standingsData = res.response[0].league.standings;

    // 월드컵(1): 그룹별 배열을 평탄화하되 group 필드 보존, 팀 ID 기준 중복 제거
    if (leagueId === 1) {
        const seen = new Set<string>();
        return standingsData.flatMap((group: any[]) =>
            group.map((s: any) => ({
                rank: s.rank, team: { id: s.team.id.toString(), name: s.team.name, logo: getTeamLogo(s.team.id) },
                played: s.all.played, won: s.all.win, drawn: s.all.draw, lost: s.all.lose,
                goalsFor: s.all.goals.for, goalsAgainst: s.all.goals.against, goalDifference: s.goalsDiff,
                points: s.points, form: s.form?.split(''), group: (s.group || '').trim()
            }))
        ).filter((item: StandingItem) => {
            if (seen.has(item.team.id)) return false;
            seen.add(item.team.id);
            return true;
        });
    }

    // K리그의 경우 스플릿 라운드로 순위 배열이 나뉘어 있을 수 있으므로 flat 처리
    if (leagueId === 292 || leagueId === 293 || leagueId === 10) {
        standingsData = [standingsData.flat()];
    }

    return standingsData[0].map((s: any) => ({
        rank: s.rank, team: { id: s.team.id.toString(), name: s.team.name, logo: getTeamLogo(s.team.id) },
        played: s.all.played, won: s.all.win, drawn: s.all.draw, lost: s.all.lose,
        goalsFor: s.all.goals.for, goalsAgainst: s.all.goals.against, goalDifference: s.goalsDiff,
        points: s.points, form: s.form?.split('')
    }));
};

export const fetchMatchResultOnly = async (matchId: string): Promise<any | null> => {
    try {
        const res = await fetchFootballAPI(`/fixtures?id=${matchId}`);
        if (!res?.response?.[0]) return null;
        const data = res.response[0];
        return {
            status: ['FT', 'AET', 'PEN'].includes(data.fixture.status.short) ? 'Finished' : 'Other',
            goals: data.goals
        };
    } catch (e) {
        return null;
    }
};

export const fetchMatchDetails = async (matchId: string, baseGame?: Game, forceRefresh: boolean = false, options: { stats?: boolean, events?: boolean, lineups?: boolean, players?: boolean, injuries?: boolean } = { stats: true, events: true, lineups: true, players: true, injuries: true }): Promise<MatchDetail | null> => {
    const cacheKey = `match_full_detail_v1_${matchId}_${JSON.stringify(options)}`;
    if (!forceRefresh) {
        const cached = getFromCache<MatchDetail>(cacheKey);
        if (cached) return cached;
    } else {
        clearCache(cacheKey);
    }
    
      try {
        const isInternalId = matchId.includes('-') || (matchId.length > 10);
        const promises: Promise<any>[] = [isInternalId ? Promise.resolve(null) : fetchFootballAPI(`/fixtures?id=${matchId}`)];
        if (options.stats && !isInternalId) promises.push(fetchFootballAPI(`/fixtures/statistics?fixture=${matchId}`));
        else promises.push(Promise.resolve(null));
        
        if (options.events && !isInternalId) promises.push(fetchFootballAPI(`/fixtures/events?fixture=${matchId}`));
        else promises.push(Promise.resolve(null));

        if (options.lineups && !isInternalId) promises.push(fetchFootballAPI(`/fixtures/lineups?fixture=${matchId}`));
        else promises.push(Promise.resolve(null));

        if (options.players && !isInternalId) promises.push(fetchFootballAPI(`/fixtures/players?fixture=${matchId}`));
        else promises.push(Promise.resolve(null));

        if (options.injuries && !isInternalId) promises.push(fetchFootballAPI(`/injuries?fixture=${matchId}`));
        else promises.push(Promise.resolve(null));

        const [res, statsRes, eventsRes, lineupsRes, playersRes, injuriesRes] = await Promise.all(promises);
        
        // 디버깅 로그 추가
        // console.log(`Match Details API Response for ${matchId}:`, {
        //     hasFixture: !!res?.response?.[0],
        //     statsCount: statsRes?.response?.length,
        //     eventsCount: eventsRes?.response?.length,
        //     lineupsCount: lineupsRes?.response?.length,
        //     playersCount: playersRes?.response?.length,
        //     injuriesCount: injuriesRes?.response?.length
        // });

        if (!res?.response?.[0]) return baseGame ? { ...baseGame, stats: [], events: [] } : null;
        const data = res.response[0];
        const f = data.fixture;
        const playerRatings = new Map<string, string>();
        if (playersRes?.response) {
            playersRes.response.forEach((team: any) => team.players.forEach((p: any) => {
                if (p.statistics?.[0]?.games?.rating) playerRatings.set(p.player.id.toString(), p.statistics[0].games.rating);
            }));
        }
        const allStatTypes = new Set<string>();
        statsRes?.response?.forEach((team: any) => {
            team.statistics?.forEach((s: any) => allStatTypes.add(s.type));
        });

        const matchStats: MatchStat[] = Array.from(allStatTypes).map(type => {
            const homeStat = statsRes?.response?.[0]?.statistics?.find((s: any) => s.type === type);
            const awayStat = statsRes?.response?.[1]?.statistics?.find((s: any) => s.type === type);
            const homeValue = homeStat?.value ?? 0;
            const awayValue = awayStat?.value ?? 0;
            return {
                label: type,
                homeValue: homeValue,
                awayValue: awayValue,
                type: String(homeValue).includes('%') || String(awayValue).includes('%') ? 'percent' : 'number'
            };
        });
        const matchEvents: MatchEvent[] = (eventsRes?.response || []).map((e: any, idx: number) => ({
            id: idx.toString(), minute: e.time.elapsed, type: e.type.toLowerCase() === 'goal' ? 'goal' : e.type.toLowerCase() === 'card' ? 'card' : 'sub',
            team: e.team.id === data.teams.home.id ? 'home' : 'away', player: e.player.name, player_id: e.player.id?.toString(),
            assist: e.assist?.name, assist_id: e.assist?.id?.toString(), detail: e.detail
        }));
        let lineups: MatchDetail['lineups'] | undefined = undefined;
        if (lineupsRes?.response && lineupsRes.response.length > 0) {
            const mapPlayers = (list: any[]) => list.map((p: any) => ({
                id: p.player?.id?.toString() || '', name: p.player?.name || '', number: p.player?.number?.toString() || '', pos: p.player?.pos || '', grid: p.player?.grid || null,
                photo: `https://media.api-sports.io/football/players/${p.player?.id}.png`, captain: p.player?.captain || p.captain || false, rating: playerRatings.get(p.player?.id?.toString())
            }));
            const formatHex = (hex: string | null) => (!hex ? '#ffffff' : hex.startsWith('#') ? hex : `#${hex}`);
            const extractColors = (teamData: any) => teamData.team.colors ? {
                player: { primary: formatHex(teamData.team.colors.player.primary), number: formatHex(teamData.team.colors.player.number), border: formatHex(teamData.team.colors.player.border) },
                goalkeeper: { primary: formatHex(teamData.team.colors.goalkeeper.primary), number: formatHex(teamData.team.colors.goalkeeper.number), border: formatHex(teamData.team.colors.goalkeeper.border) }
            } : undefined;
            lineups = {
                home: { 
                    formation: lineupsRes.response[0]?.formation || 'Unknown', 
                    coach: { id: lineupsRes.response[0]?.coach?.id?.toString(), name: lineupsRes.response[0]?.coach?.name, photo: lineupsRes.response[0]?.coach?.photo }, 
                    startXI: mapPlayers(lineupsRes.response[0]?.startXI || []), 
                    substitutes: mapPlayers(lineupsRes.response[0]?.substitutes || []), 
                    colors: lineupsRes.response[0] ? extractColors(lineupsRes.response[0]) : undefined 
                },
                away: { 
                    formation: lineupsRes.response[1]?.formation || 'Unknown', 
                    coach: { id: lineupsRes.response[1]?.coach?.id?.toString(), name: lineupsRes.response[1]?.coach?.name, photo: lineupsRes.response[1]?.coach?.photo }, 
                    startXI: mapPlayers(lineupsRes.response[1]?.startXI || []), 
                    substitutes: mapPlayers(lineupsRes.response[1]?.substitutes || []), 
                    colors: lineupsRes.response[1] ? extractColors(lineupsRes.response[1]) : undefined 
                }
            };
        }
        const statusShort = f.status.short;
        let status: Game['status'] = 'Scheduled';
        if (['FT', 'AET', 'PEN'].includes(statusShort)) status = 'Finished';
        else if (['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(statusShort)) status = 'Live';

        const result: MatchDetail = {
            ...(baseGame || {} as any), id: f.id.toString(), league: data.league.id.toString(), leagueName: data.league.name,
            leagueLogo: getLeagueLogo(data.league.id),
            homeTeam: { name: data.teams.home.name, logo: getTeamLogo(data.teams.home.id), id: data.teams.home.id.toString() },
            awayTeam: { name: data.teams.away.name, logo: getTeamLogo(data.teams.away.id), id: data.teams.away.id.toString() },
            venue: f.venue.name, venueCity: f.venue.city, venueId: f.venue.id, attendance: f.attendance?.total ? String(f.attendance.total) : 'Unknown',
            stats: matchStats, events: matchEvents, goals: data.goals, lineups,
            injuries: injuriesRes?.response || [],
            dateLabel: new Date(f.date).toLocaleDateString('sv-SE'),
            time: new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
            score: status !== 'Scheduled' ? `${data.goals.home}:${data.goals.away}` : undefined, status,
            elapsed: f.status.elapsed,
            scoreDetails: { halftime: data.score.halftime, fulltime: data.score.fulltime, extratime: data.score.extratime, penalty: data.score.penalty }, round: data.league.round
        };
        saveToCache(cacheKey, result, 1000 * 60 * 5); 
        return result;
    } catch (e) { return baseGame ? { ...baseGame, stats: [], events: [] } : null; }
};

export const fetchSupabaseNewsList = async (lang: Language, page: number = 0, pageSize: number = 10, date?: string, category?: string, importance?: string): Promise<{ news: NewsItem[], nextPage?: number }> => {
    // 캐시 키 변경 (stadium 필터 포함 버전)
    const cacheKey = `sb_news_list_v7_stadium_excluded_${lang}_${page}_${date || 'latest'}_${category || 'all'}`;
    const cachedNews = getFromCache<{ news: NewsItem[], nextPage?: number }>(cacheKey);
    if (cachedNews) return cachedNews;
    try {
        const from = page * pageSize; 
        const to = from + pageSize - 1;
        let query = supabase.from('news_list').select('*').eq('is_hidden', false).neq('type', 'stadium').neq('type', 'analysis'); // is_hidden이 false이고 스타디움이 아닌 것만 조회
        
        if (date) {
            const kstStartDate = new Date(`${date}T00:00:00+09:00`);
            const kstEndDate = new Date(`${date}T23:59:59+09:00`);
            query = query.gte('published_at', kstStartDate.toISOString()).lte('published_at', kstEndDate.toISOString());
        }
        if (category && category !== 'latest') query = query.eq('type', category);
        else query = query.neq('type', 'gossip');
        if (importance) query = query.eq('importance', importance);

        const { data: rawNews, error } = await query.order('published_at', { ascending: false }).range(from, to);
        if (error) throw error;

        const userIds = Array.from(new Set((rawNews || []).filter(n => n.user_id).map(n => n.user_id)));
        const { data: userData } = userIds.length > 0 ? await supabase.from('user').select('id, name, avatar_url').in('id', userIds) : { data: [] };
        const userMap = new Map<string, any>((userData || []).map(u => [u.id, u]));

        const news: NewsItem[] = (rawNews || []).map((item: any) => {
            const newsItem = mapRawNewsToItem(item, lang);
            if (userMap.has(item.user_id)) {
                const u = userMap.get(item.user_id);
                newsItem.author = { name: u.name, avatar: u.avatar_url };
            }
            return newsItem;
        });

        const result = { news, nextPage: news.length === pageSize ? page + 1 : undefined };
        saveToCache(cacheKey, result, 1000 * 60 * 5); 
        return result;
    } catch (error) { return { news: [] }; }
};

export const fetchSupabaseNewsItem = async (newsId: string, lang: Language): Promise<NewsItem | null> => {
    try {
        const { data: item, error } = await supabase.from('news_list').select('*').eq('id', newsId).maybeSingle();
        if (error || !item) return null;
        let author = undefined;
        if (item.user_id) {
            const { data: u } = await supabase.from('user').select('name, avatar_url').eq('id', item.user_id).maybeSingle();
            if (u) author = { name: u.name, avatar: u.avatar_url };
        }
        const newsItem = mapRawNewsToItem(item, lang);
        newsItem.author = author;
        return newsItem;
    } catch (e) { return null; }
};

export const searchNews = async (query: string, lang: Language = 'en'): Promise<NewsItem[]> => {
    try {
        const { data: rawNews, error } = await supabase.from('news_list')
            .select('*')
            .eq('is_hidden', false) // is_hidden이 false인 것만 조회
            .neq('type', 'stadium') // 스타디움 게시물 제외
            .neq('type', 'analysis') // 분석 게시품 제외
            .or(`title_ko.ilike.%${query}%,title_en.ilike.%${query}%`)
            .limit(30);
        if (error) return [];
        const userIds = Array.from(new Set((rawNews || []).filter(n => n.user_id).map(n => n.user_id)));
        const { data: userData } = userIds.length > 0 ? await supabase.from('user').select('id, name, avatar_url').in('id', userIds) : { data: [] };
        const userMap = new Map<string, any>((userData || []).map(u => [u.id, u]));

        return rawNews.map((item: any) => {
            const newsItem = mapRawNewsToItem(item, lang);
            if (userMap.has(item.user_id)) {
                const u = userMap.get(item.user_id);
                newsItem.author = { name: u.name, avatar: u.avatar_url };
            }
            return newsItem;
        });
    } catch (e) { return []; }
};

export const getLatestTransfers = async (page: number = 0, pageSize: number = 100): Promise<TransferNews[]> => {
    try {
        const { data: groups } = await supabase.from('transfer_groups').select('id').eq('type', 'latest').order('id', { ascending: false });
        if (groups && groups.length > 0) {
            const groupIds = groups.map(g => g.id.toString());
            const from = page * pageSize;
            const to = from + pageSize - 1;
            const { data: items } = await supabase.from('transfer_items').select('*').in('group_id', groupIds).order('id', { ascending: false }).range(from, to);
            const transfers = (items || []).map((t: any) => mapDBToTransferNews(t));
            
            // transferdate 최신순으로 정렬
            return transfers.sort((a, b) => {
                const parseDate = (d: string | undefined) => {
                    if (!d) return 0;
                    // DD/MM/YYYY 형식 처리
                    if (d.includes('/')) {
                        const [day, month, year] = d.split('/').map(Number);
                        return new Date(year, month - 1, day).getTime();
                    }
                    return new Date(d).getTime();
                };

                const dateA = parseDate(a.date);
                const dateB = parseDate(b.date);
                
                if (dateB !== dateA) return dateB - dateA;
                
                // 날짜가 같으면 ID 역순 정렬
                const idA = parseInt(a.id) || 0;
                const idB = parseInt(b.id) || 0;
                return idB - idA;
            });
        }
    } catch (e) {}
    return [];
};

const mapDBToTransferNews = (dbItem: any): TransferNews => {
    const name = dbItem.player || dbItem.name || 'Unknown';
    const joined = dbItem.joinedteam || 'Unknown';
    return {
        id: dbItem.id?.toString() || Math.random().toString(),
        playerId: dbItem.player_id?.toString() || dbItem.playerId?.toString() || `search-${name}|${joined}`,
        player: { 
            name, 
            avatar: dbItem.playerface || dbItem.image || '', 
            prevClub: dbItem.left || 'Unknown', 
            prevClubLogo: dbItem.left_id ? getTeamLogo(dbItem.left_id) : (dbItem.leftemblem || '') 
        },
        toClub: { 
            name: joined, 
            logo: dbItem.joined_id ? getTeamLogo(dbItem.joined_id) : (dbItem.joinedemblem || '') 
        },
        fee: dbItem.fee || 'Undisclosed', marketValue: dbItem.marketvalue || dbItem.market_value, 
        date: dbItem.transferdate || dbItem.transfer_date , status: 'confirmed', league: dbItem.leaguegroup || dbItem.league_group || dbItem.league
    };
};

export const getTopTransferGroups = async (): Promise<TransferGroup[]> => {
    try {
        const { data } = await supabase.from('transfer_groups').select('*').eq('type', 'top').order('season', { ascending: false });
        if (!data) return [];
        return data.filter(g => g.season).sort((a, b) => {
            const seasonCompare = String(b.season).localeCompare(String(a.season));
            if (seasonCompare !== 0) return seasonCompare;
            
            // 같은 시즌이면 윈도우 정렬 (all -> summer -> winter 순서로 노출되게 하려면 반대로 정렬)
            const windowOrder: Record<string, number> = { 'all': 0, 'winter': 1, 'summer': 2 };
            return (windowOrder[a.window || 'all'] || 0) - (windowOrder[b.window || 'all'] || 0);
        });
    } catch (e) { return []; }
};

export const getTransfersByGroupId = async (groupId: number | string): Promise<TransferNews[]> => {
    try {
        const { data } = await supabase.from('transfer_items').select('*').eq('group_id', String(groupId));
        return (data || []).map((t: any) => mapDBToTransferNews(t));
    } catch (e) { return []; }
};

export const fetchUserLeagueSelectionsFromDB = async (userId: string): Promise<{ league_id: string, order: number }[]> => {
    try {
        const { data, error } = await supabase.from('league_selections').select(`league_id, order`).eq('user_id', userId).order('order', { ascending: true });
        return error ? [] : data ? data.map((item: any) => ({ league_id: String(item.league_id), order: item.order || 0 })) : [];
    } catch (e) { return []; }
};

export const saveUserLeagueSelectionsToDB = async (userId: string, selections: { league_id: string, order: number }[]): Promise<void> => {
    try {
        await supabase.from('league_selections').delete().eq('user_id', userId);
        if (selections.length > 0) {
            const { error } = await supabase.from('league_selections').insert(selections.map(s => ({ user_id: userId, league_id: s.league_id, order: s.order })));
            if (error) throw error;
        }
    } catch (e) { throw e; }
};

export const fetchLeaguesByIds = async (ids: string[]): Promise<LeagueDB[]> => {
    if (!ids || ids.length === 0) return [];
    try {
        const res = await fetchFootballAPI(`/leagues?id=${ids.join(',')}`);
        if (res?.response) return res.response.map((item: any) => ({ id: item.league.id.toString(), name: item.league.name, logo_url: getLeagueLogo(item.league.id), country: item.country?.code || 'UN' }));
        return [];
    } catch (e) { return []; }
};

export const searchLeagues = async (query: string): Promise<LeagueDB[]> => {
    if (!query || query.length < 2) return [];
    try {
        const res = await fetchFootballAPI(`/leagues?search=${encodeURIComponent(query)}`);
        if (res?.response) return res.response.map((item: any) => ({ id: item.league.id.toString(), name: item.league.name, logo_url: getLeagueLogo(item.league.id), country: item.country?.code || 'UN' }));
        return [];
    } catch (e) { return []; }
};

export const fetchPlayerStatsForModal = async (id: string, season: string): Promise<any> => {
    try {
        const res = await fetchFootballAPI(`/players?id=${id}&season=${season}`);
        return res?.response?.[0] || null;
    } catch (e) { return null; }
};

export const fetchMatchPlayerStats = async (fixtureId: string, playerId: string): Promise<any> => {
    try {
        const res = await fetchFootballAPI(`/fixtures/players?fixture=${fixtureId}`);
        if (!res?.response) return null;
        for (const teamData of res.response) {
            const foundPlayer = teamData.players.find((p: any) => p.player.id.toString() === playerId);
            if (foundPlayer) return { player: foundPlayer.player, statistics: foundPlayer.statistics, isMatchSpecific: true };
        }
        return null;
    } catch (e) { return null; }
};

export const fetchPlayerProfileForModal = async (id: string): Promise<any> => {
    try {
        const res = await fetchFootballAPI(`/players/profiles?player=${id}`);
        return res?.response?.[0] || null;
    } catch (e) { return null; }
};

export const fetchCoachProfile = async (id: string): Promise<any> => {
    try {
        const res = await fetchFootballAPI(`/coachs?id=${id}`);
        return res?.response?.[0] || null;
    } catch (e) { return null; }
};

export const fetchTeamStatistics = async (teamId: string, leagueId: string, season: string): Promise<any> => {
    const res = await fetchFootballAPI(`/teams/statistics?league=${leagueId}&team=${teamId}&season=${season}`);
    return res?.response || null;
};

export const fetchTeamTransfers = async (teamId: string): Promise<any[]> => {
    const res = await fetchFootballAPI(`/transfers?team=${teamId}`);
    return res?.response || [];
};

export const fetchTeamInfo = async (teamId: string): Promise<any> => {
    const res = await fetchFootballAPI(`/teams?id=${teamId}`);
    return res?.response?.[0] || null;
};

export const fetchTeamSquad = async (teamId: string): Promise<any[]> => {
    const res = await fetchFootballAPI(`/players/squads?team=${teamId}`);
    return res?.response?.[0]?.players || [];
};

export const fetchRecentLineupPlayers = async (teamId: string): Promise<any[]> => {
    try {
        let fixtureId;
        const res = await fetchFootballAPI(`/fixtures?team=${teamId}&last=1&status=FT`);
        
        if (res?.response?.length > 0 && res.response[0].fixture) {
            fixtureId = res.response[0].fixture.id;
        } else {
            // 리그 일정 등의 제약으로 최근 1경기(FT)가 없으면 가장 가까운 일정을 가져옴
            const anyRes = await fetchFootballAPI(`/fixtures?team=${teamId}&last=1`);
            if (anyRes?.response?.length > 0 && anyRes.response[0].fixture) {
                fixtureId = anyRes.response[0].fixture.id;
            } else {
                return [];
            }
        }
        
        const statsRes = await fetchFootballAPI(`/fixtures/players?fixture=${fixtureId}&team=${teamId}`);
        if (!statsRes?.response?.[0]) {
            // 만약 통계가 비어 있다면 스쿼드로 대체 조회 시도
            const squadRes = await fetchTeamSquad(teamId);
            if (squadRes && squadRes.length > 0) {
                 return squadRes.map((p: any) => ({
                     player: { id: p.id, name: p.name, photo: p.photo || `https://media.api-sports.io/football/players/${p.id}.png` },
                     statistics: [{ games: { position: POSITION_MAP[p.position] || p.position || 'Midfielder', rating: null } }],
                     isRecentStarter: false
                 }));
            }
            return [];
        }
        return statsRes.response[0].players.map((p: any) => ({
            player: { id: p.player.id, name: p.player.name, photo: p.player.photo || `https://media.api-sports.io/football/players/${p.player.id}.png` },
            statistics: [{ games: { position: POSITION_MAP[p.statistics[0].games.position] || p.statistics[0].games.position || 'Midfielder', rating: p.statistics[0].games.rating || null } }],
            isRecentStarter: true
        }));
    } catch (e) { return []; }
};

const POSITION_MAP: Record<string, string> = { 'G': 'Goalkeeper', 'D': 'Defender', 'M': 'Midfielder', 'A': 'Attacker', 'F': 'Attacker' };

export const incrementNewsView = async (newsId: string, lang: Language, userId: string | null): Promise<void> => {
    try { await supabase.from('news_views').insert({ news_id: newsId, user_id: userId, lang }); } catch (e) {}
};

export const getNewsViewCount = async (newsId: string): Promise<number> => {
    try {
        const { count, error } = await supabase.from('news_views').select('*', { count: 'exact', head: true }).eq('news_id', newsId);
        return error ? 0 : count || 0;
    } catch (e) { return 0; }
};

export const getNewsLikes = async (newsId: string): Promise<any[]> => {
    try {
        const { data, error } = await supabase.from('news_likes').select('*').eq('news_id', newsId);
        if (error) throw error;
        return data || [];
    } catch (e) { return []; }
};

// Fix: Added missing fetchTopScorers function
export const fetchTopScorers = async (leagueId: string, season: string): Promise<any[]> => {
    try {
        const res = await fetchFootballAPI(`/players/topscorers?league=${leagueId}&season=${season}`);
        return res?.response || [];
    } catch (e) { return []; }
};

// Fix: Added missing fetchTopAssists function
export const fetchTopAssists = async (leagueId: string, season: string): Promise<any[]> => {
    try {
        const res = await fetchFootballAPI(`/players/topassists?league=${leagueId}&season=${season}`);
        return res?.response || [];
    } catch (e) { return []; }
};

// Fix: Added missing fetchVenueDetails function
export const fetchVenueDetails = async (venueId: number): Promise<any> => {
    try {
        const res = await fetchFootballAPI(`/venues?id=${venueId}`);
        return res?.response?.[0] || null;
    } catch (e) { return null; }
};

export const fetchLeagueInjuries = async (leagueId: string, season: string): Promise<any[]> => {
    try {
        const res = await fetchFootballAPI(`/injuries?league=${leagueId}&season=${season}`);
        return res?.response || [];
    } catch (e) { return []; }
};

export const fetchPredictions = async (fixtureId: string): Promise<any | null> => {
    try {
        const res = await fetchFootballAPI(`/predictions?fixture=${fixtureId}`);
        return res?.response?.[0] || null;
    } catch (e) {
        console.error("Fetch Predictions Error:", e);
        return null;
    }
};

export const fetchH2H = async (h2h: string, limit: number = 10): Promise<any[]> => {
    const cacheKey = `h2h_${h2h}_v3_${limit}`;
    const cached = getFromCache<any[]>(cacheKey);
    if (cached) return cached;

    try {
        const res = await fetchFootballAPI(`/fixtures/headtohead?h2h=${h2h}&last=${limit}`);
        const data = res?.response || [];
        if (data.length > 0) {
            saveToCache(cacheKey, data, 1000 * 60 * 60 * 24); // 24 hours cache
        }
        return data;
    } catch (e) {
        console.error("Fetch H2H Error:", e);
        return [];
    }
};

const CALENDAR_YEAR_LEAGUES = ['1', '292', '293', '253', '98', '71', '16', '262', '179', '10'];

export const getLeagueSeason = (leagueId: string, date: Date = new Date()): string => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // 춘추자 리그 (K리그, MLS 등)
    if (CALENDAR_YEAR_LEAGUES.includes(leagueId)) {
        return year.toString();
    }
    
    // 추동계 리그 (유럽 리그 등)
    // 7월(month 6) 이전이면 이전 연도를 시즌 시작으로 봄 (예: 2026년 1월은 2025 시즌)
    return month < 6 ? (year - 1).toString() : year.toString();
};

export const getLeagueSeasonLabel = (leagueId: string, season: string): string => {
    if (CALENDAR_YEAR_LEAGUES.includes(leagueId)) {
        return season;
    }
    const nextYear = (parseInt(season) + 1).toString().slice(-2);
    return `${season}/${nextYear}`;
};
